// Practice/Scaffold answer card. Worked examples are browsable up front, but
// the hint follows the same criteria as exams: it unlocks ONLY after a wrong
// attempt (the server reveals it in the attempt response — it never ships in
// the initial question payload). No timer, immediate feedback after each answer.
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiGet } from '@/lib/api';
import type { BinColor, Question, WorkedExample } from '@/types';
import { BIN_LABEL } from '@/lib/bins';
import { MathText } from '@/components/exam/MathText';
import { MCQInput } from '@/components/exam/MCQInput';
import { WatchVideosButton } from '@/components/video/VideoLibrary';

type SubmitResult = {
  isCorrect: boolean;
  bin?: BinColor;
  support?: { tipsText?: string; theoryRef?: string; videoRef?: string } | null;
};

interface Props {
  question: Question;
  /** Subject/topic tag row shown above the question — purely presentational. */
  subjectLabel?: string;
  topicLabel?: string;
  onSubmit: (selectedIdx: number) => Promise<SubmitResult>;
  onNext?: () => void;
  onAnother?: () => void;
  onScaffold?: () => void;
  nextLabel?: string;
}

type Tab = 'help' | 'examples';

const BIN_TONE: Record<BinColor, { bg: string; text: string }> = {
  GREEN: { bg: 'var(--green-bg)', text: 'var(--green-text)' },
  PURPLE: { bg: 'var(--purple-bg)', text: 'var(--purple-text)' },
  BLUE: { bg: 'var(--blue-bg)', text: 'var(--blue-text)' },
  ORANGE: { bg: 'var(--orange-bg)', text: 'var(--orange-text)' },
  PINK: { bg: 'var(--pink-bg)', text: 'var(--pink-text)' },
};

export function PracticeAnswerCard({
  question,
  subjectLabel,
  topicLabel,
  onSubmit,
  onNext,
  onAnother,
  onScaffold,
  nextLabel = 'Next question',
}: Props) {
  const [tab, setTab] = useState<Tab>('help');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<SubmitResult | null>(null);

  const examples = useQuery({
    queryKey: ['examples', question.id],
    enabled: tab === 'examples',
    queryFn: () => apiGet<WorkedExample[]>(`/questions/${question.id}/examples`),
  });

  async function handleSubmit(idx: number) {
    setSubmitting(true);
    try {
      setResult(await onSubmit(idx));
    } finally {
      setSubmitting(false);
    }
  }

  const resultTone = result ? BIN_TONE[result.bin ?? (result.isCorrect ? 'GREEN' : 'PINK')] : null;

  return (
    <div className="rounded-[22px] p-6" style={{ background: 'var(--surface-card)', border: '1px solid var(--border-default)' }}>
      {(subjectLabel || topicLabel) && (
        <div className="mb-3.5 flex items-center gap-2.5">
          {subjectLabel && (
            <span
              className="rounded-full px-2.5 py-1 text-fs-11.5 font-bold"
              style={{ background: 'var(--paper-sunk)', color: 'var(--ink-2)' }}
            >
              {subjectLabel}
            </span>
          )}
          {topicLabel && (
            <span className="text-xs font-semibold" style={{ color: 'var(--ink-3)' }}>
              {topicLabel}
            </span>
          )}
        </div>
      )}

      <div className="text-fs-19 font-bold leading-snug tracking-tight" style={{ color: 'var(--text-strong)' }}>
        <MathText>{question.text || 'Question'}</MathText>
      </div>

      {/* Helplines are GATED: the hint unlocks only after a wrong attempt. */}
      <div className="mt-4 flex gap-2 text-sm">
        <button
          onClick={() => setTab('help')}
          className="rounded-full px-3.5 py-1.5 text-fs-13 font-bold transition"
          style={tab === 'help' ? { background: 'var(--brand-forest)', color: '#fff' } : { background: 'var(--paper-sunk)', color: 'var(--ink-2)' }}
        >
          Helplines
        </button>
        <button
          onClick={() => setTab('examples')}
          className="rounded-full px-3.5 py-1.5 text-fs-13 font-bold transition"
          style={tab === 'examples' ? { background: 'var(--brand-forest)', color: '#fff' } : { background: 'var(--paper-sunk)', color: 'var(--ink-2)' }}
        >
          Worked examples
        </button>
      </div>

      {tab === 'help' ? (
        <div className="mt-2.5 space-y-2 rounded-2xl p-3.5 text-sm" style={{ background: 'var(--surface-sunk)' }}>
          {!result ? (
            <p style={{ color: 'var(--ink-3)' }}>🔒 Give it a try first — a hint unlocks after your attempt.</p>
          ) : result.isCorrect ? (
            <p style={{ color: 'var(--ink-3)' }}>You got it right — no hint needed! 🎉</p>
          ) : (
            <>
              {result.support?.tipsText && <p style={{ color: 'var(--text-body)' }}>💡 {result.support.tipsText}</p>}
              {result.support?.theoryRef && <p style={{ color: 'var(--ink-2)' }}>📖 Theory: {result.support.theoryRef}</p>}
              {result.support?.videoRef && (
                <WatchVideosButton source={{ videoRef: result.support.videoRef }} title="Concept videos" className="text-left underline">
                  <span style={{ color: 'var(--info)' }}>📹 Watch concept videos</span>
                </WatchVideosButton>
              )}
              {!result.support?.tipsText && !result.support?.theoryRef && !result.support?.videoRef && (
                <p style={{ color: 'var(--ink-3)' }}>No extra helplines for this question.</p>
              )}
            </>
          )}
        </div>
      ) : (
        <div className="mt-2.5 space-y-2 rounded-2xl p-3.5 text-sm" style={{ background: 'var(--surface-sunk)' }}>
          {examples.isLoading ? (
            <p style={{ color: 'var(--ink-3)' }}>Loading examples…</p>
          ) : (examples.data ?? []).length === 0 ? (
            <p style={{ color: 'var(--ink-3)' }}>No worked examples available.</p>
          ) : (
            (examples.data ?? []).map((ex, i) => (
              <div key={i} className="rounded-lg p-2" style={{ background: 'var(--surface-card)' }}>
                {ex.step && <p className="font-semibold" style={{ color: 'var(--text-body)' }}>{ex.step}</p>}
                {ex.explanation && <p style={{ color: 'var(--ink-2)' }}>{ex.explanation}</p>}
                {!ex.step && !ex.explanation && (
                  <pre className="whitespace-pre-wrap text-xs" style={{ color: 'var(--ink-2)' }}>{JSON.stringify(ex)}</pre>
                )}
              </div>
            ))
          )}
        </div>
      )}

      {/* Input + feedback */}
      <div className="mt-5">
        {result ? (
          <div className="space-y-3">
            <div className="rounded-2xl p-4 text-center" style={{ background: resultTone!.bg, color: resultTone!.text }}>
              <p className="text-lg font-extrabold">{result.isCorrect ? 'Correct!' : 'Not quite — keep going'}</p>
              {result.bin && <p className="text-sm font-semibold opacity-90">{BIN_LABEL[result.bin]}</p>}
            </div>
            {/* The earned hint — unlocked by this wrong attempt. */}
            {!result.isCorrect && result.support?.tipsText && (
              <div className="rounded-xl p-3 text-sm font-medium" style={{ background: 'var(--orange-bg)', color: 'var(--orange-text)' }}>
                💡 Hint: {result.support.tipsText}
              </div>
            )}
            <div className="flex flex-wrap gap-2">
              {onNext && (
                <button
                  onClick={onNext}
                  className="flex-1 rounded-2xl px-4 py-3 font-bold text-white transition"
                  style={{ background: 'var(--action-bg)' }}
                >
                  {nextLabel}
                </button>
              )}
              {onAnother && (
                <button
                  onClick={onAnother}
                  className="flex-1 rounded-2xl border px-4 py-3 font-bold transition"
                  style={{ borderColor: 'var(--border-strong)', color: 'var(--text-body)' }}
                >
                  Try another on this topic
                </button>
              )}
              {onScaffold && (
                <button
                  onClick={onScaffold}
                  className="flex-1 rounded-2xl border px-4 py-3 font-bold transition"
                  style={{ borderColor: 'var(--border-strong)', color: 'var(--text-body)' }}
                >
                  Go to scaffold
                </button>
              )}
            </div>
          </div>
        ) : (
          <MCQInput options={question.options ?? []} submitting={submitting} onSubmit={handleSubmit} />
        )}
      </div>
    </div>
  );
}
